import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  voteTargetKey,
  type ActionItemView,
  type CommentView,
  type RetroSnapshot,
} from '@retro/shared';
import { Avatar } from '../ui.js';
import { TopicChipList } from './TopicChips.js';
import { ActionItemDialog, ActionItemRow } from './ActionItemDialog.js';

type DiscussionItem = {
  key: string;
  columnId: string;
  votes: number;
  groupTitle: string | null;
  comments: CommentView[];
  topicIds: string[];
};

const VOTE_FILTERS = [
  { value: 0, label: 'All comments' },
  { value: 1, label: '1+ vote' },
  { value: 2, label: '2+ votes' },
  { value: 3, label: '3+ votes' },
];

/**
 * Step 4. Discussion, ordered by what the team actually voted for, with the
 * action plan alongside so decisions get captured while they are being made.
 */
export function ActionPlanPhase({ snapshot }: { snapshot: RetroSnapshot }) {
  const isFacilitator = snapshot.me.isFacilitator;
  const [minVotes, setMinVotes] = useState(1);
  const [topicFilter, setTopicFilter] = useState('ALL');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [editing, setEditing] = useState<ActionItemView | null>(null);
  const [creatingFor, setCreatingFor] = useState<string[] | null>(null);

  const items = useMemo<DiscussionItem[]>(() => {
    const result: DiscussionItem[] = [];

    for (const group of snapshot.groups) {
      const comments = snapshot.comments
        .filter((comment) => comment.groupId === group.id)
        .sort((a, b) => a.order - b.order);
      if (comments.length === 0) continue;

      result.push({
        key: `g:${group.id}`,
        columnId: group.columnId,
        votes: snapshot.votes.totals[voteTargetKey({ type: 'GROUP', id: group.id })] ?? 0,
        groupTitle: group.title || 'Grouped comments',
        comments,
        topicIds: [...new Set(comments.flatMap((comment) => comment.topicIds))],
      });
    }

    for (const comment of snapshot.comments) {
      if (comment.groupId) continue;
      result.push({
        key: `c:${comment.id}`,
        columnId: comment.columnId,
        votes: snapshot.votes.totals[voteTargetKey({ type: 'COMMENT', id: comment.id })] ?? 0,
        groupTitle: null,
        comments: [comment],
        topicIds: comment.topicIds,
      });
    }

    // Most-voted first: the point of voting was to set this order.
    return result.sort((a, b) => b.votes - a.votes || a.comments[0]!.order - b.comments[0]!.order);
  }, [snapshot.comments, snapshot.groups, snapshot.votes.totals]);

  const filtered = items.filter(
    (item) =>
      item.votes >= minVotes &&
      !hiddenColumns.includes(item.columnId) &&
      (topicFilter === 'ALL' || item.topicIds.includes(topicFilter)),
  );

  const columnStats = snapshot.columns.map((column) => ({
    column,
    votes: items.filter((item) => item.columnId === column.id).reduce((sum, item) => sum + item.votes, 0),
    comments: snapshot.comments.filter((comment) => comment.columnId === column.id).length,
  }));

  // Matches per topic under the current vote/column filters, so the picker
  // shows which topics actually have something behind them before selecting.
  const topicCounts = new Map<string, number>();
  for (const item of items) {
    if (item.votes < minVotes || hiddenColumns.includes(item.columnId)) continue;
    for (const topicId of item.topicIds) {
      topicCounts.set(topicId, (topicCounts.get(topicId) ?? 0) + 1);
    }
  }

  return (
    <>
      <div className="filters">
        <select
          className="input input-compact w-auto"
          value={minVotes}
          onChange={(event) => setMinVotes(Number(event.target.value))}
          aria-label="Filter by votes"
        >
          {VOTE_FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>

        <select
          className="input input-compact w-auto"
          value={topicFilter}
          onChange={(event) => setTopicFilter(event.target.value)}
          aria-label="Filter by topic"
        >
          <option value="ALL">All topics</option>
          {snapshot.topics.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {`${topic.name} (${topicCounts.get(topic.id) ?? 0})`}
            </option>
          ))}
        </select>

        <span className="flex flex-wrap items-center gap-1.5" aria-label="Show or hide columns">
          {columnStats.map(({ column }) => {
            const visible = !hiddenColumns.includes(column.id);
            return (
              <button
                key={column.id}
                type="button"
                className="chip"
                aria-pressed={visible}
                title={`${visible ? 'Hide' : 'Show'} the ${column.title} column`}
                onClick={() =>
                  setHiddenColumns((current) =>
                    visible ? [...current, column.id] : current.filter((id) => id !== column.id),
                  )
                }
              >
                {column.title}
              </button>
            );
          })}
        </span>
      </div>

      <div className="split min-h-0 flex-1 overflow-auto">
        {/* Top topics — ranked by what the team voted for. */}
        <section className="card">
          <div className="pad">
            <h3 className="h3">Top topics</h3>
            <p className="text-[12px] text-muted">Ranked by total team votes</p>
          </div>

          <ol className="list">
            {filtered.length === 0 ? (
              <li className="empty">
                <strong>Nothing matches those filters</strong>
                <p>Lower the vote threshold to see more of the board.</p>
              </li>
            ) : (
              filtered.map((item, index) => {
                const column = snapshot.columns.find((c) => c.id === item.columnId);
                const title = item.groupTitle ?? item.comments[0]!.text;
                return (
                  <li key={item.key} className="border-t border-line px-4 py-3">
                    <article className="flex items-start gap-3">
                      <span className="rank">{index + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold">{title}</p>
                        <p className="mt-0.5 text-[12px] text-muted">
                          <span
                            aria-label={`${item.votes} ${item.votes === 1 ? 'vote' : 'votes'}`}
                          >
                            {item.votes} {item.votes === 1 ? 'vote' : 'votes'}
                          </span>
                          {item.comments.length > 1 ? ` · ${item.comments.length} cards` : ''}
                          {column ? ` · ${column.title}` : ''}
                        </p>

                        {item.comments.length > 1 || item.groupTitle ? (
                          <ul className="mt-2 grid gap-1.5">
                            {item.comments.map((comment) => (
                              <li key={comment.id} className="rounded-lg bg-subtle px-2.5 py-2">
                                <p className="whitespace-pre-wrap break-words text-[13px]">
                                  {comment.text}
                                </p>
                                <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted">
                                  {comment.isAnonymous ? (
                                    'Anonymous'
                                  ) : (
                                    <>
                                      <Avatar
                                        emoji={comment.author?.avatar ?? ''}
                                        name={comment.author?.name ?? ''}
                                        size="xs"
                                      />
                                      {comment.author?.name}
                                    </>
                                  )}
                                </p>
                              </li>
                            ))}
                          </ul>
                        ) : null}

                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                          <TopicChipList topicIds={item.topicIds} topics={snapshot.topics} />
                          {isFacilitator ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => {
                                setEditing(null);
                                setCreatingFor(item.comments.map((comment) => comment.id));
                              }}
                            >
                              <Plus size={14} aria-hidden /> Action
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })
            )}
          </ol>
        </section>

        {/* Agreed actions */}
        <section className="card self-start">
          <div className="pad row-between wrap">
            <div>
              <h3 className="h3">Agreed actions</h3>
              <p className="text-[12px] text-muted">Added to the team action board</p>
            </div>
            {isFacilitator ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setEditing(null);
                  setCreatingFor([]);
                }}
              >
                <Plus size={14} aria-hidden /> Add action
              </button>
            ) : null}
          </div>

          {snapshot.actionItems.length === 0 ? (
            <div className="empty">
              <strong>No actions yet</strong>
              <p>Pick a topic and turn it into a concrete next step.</p>
            </div>
          ) : (
            <ul className="list grid gap-2.5 px-4 pb-4">
              {snapshot.actionItems.map((action) => (
                <ActionItemRow key={action.id} action={action} onEdit={isFacilitator ? () => setEditing(action) : undefined} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <ActionItemDialog
        open={editing !== null || creatingFor !== null}
        onClose={() => {
          setEditing(null);
          setCreatingFor(null);
        }}
        existing={editing}
        members={snapshot.assignableMembers}
        topics={snapshot.topics}
        comments={snapshot.comments}
        initialLinkedCommentIds={creatingFor ?? []}
      />
    </>
  );
}
